import React, { FC, useCallback, useMemo } from 'react';
import { Controller, ControllerRenderProps, Validate } from 'react-hook-form';
import { IFormField } from 'modules/core/types';
import { useUncontrolledInput } from 'modules/core/hooks';
import { requiredValidator } from 'modules/core/validators';
import { Spiner } from 'modules/core/components/Spiner/Spiner';
import { calculateDropdownWidth } from 'utils/textWidth';

import { DefaultToggle, InputGroupUI, Menu, MenuItem, RacDropdown } from './styles';

interface IDropdown extends IFormField {
  placeholder?: string;
  options?: any[];
  name: string;
  required?: boolean;
  disabled?: boolean;
  isInvalid?: boolean;
  isWarning?: boolean;
  initialValue?: any;
  showPlaceholder?: boolean;
  rules?: any;
  isComponentAnimated?: boolean;
  isSpinerAnimated?: boolean;
  changeCallback?: (() => void) | undefined;
  validate?: Validate | Record<string, Validate> | undefined;
  autoWidth?: boolean; // Enable auto-width based on longest option
}

export const Dropdown: FC<IDropdown> = ({
  name,
  options,
  placeholder,
  disabled = false,
  isInvalid = false,
  isWarning = false,
  showPlaceholder,
  rules,
  isComponentAnimated = false,
  isSpinerAnimated = false,
  changeCallback,
  validate,
  onToggle,
  autoWidth = false, // Default to false for backward compatibility
}) => {
  const { controlField, registerInput, errorMessage, hasError } = useUncontrolledInput(name);

  const renderOptions = useCallback(
    ({ onChange }: ControllerRenderProps) =>
      options?.map((option: { value: string; title: string }) => (
        <MenuItem
          onClick={() => {
            onChange(option);
          }}
          onBlur={() => {
            changeCallback && changeCallback();
          }}
          key={option.value}
          title={option.title}
        >
          {option.title}
        </MenuItem>
      )),
    [changeCallback, options],
  );

  const commonInputProps = useMemo(
    () => ({
      name,
      $isInvalid: Boolean(errorMessage) || hasError || isInvalid,
      $isWarning: isWarning,
      disabled: disabled,
      $isAnimated: isComponentAnimated,
      $autoWidth: autoWidth && options ? calculateDropdownWidth(options) : undefined,
    }),
    [disabled, errorMessage, hasError, isComponentAnimated, name, isInvalid, isWarning, autoWidth, options],
  );

  const renderDropdown = useCallback(
    (renderProps) => (
      <InputGroupUI $isPlaceholder={!renderProps?.value?.title || showPlaceholder} {...commonInputProps}>
        <RacDropdown disabled={disabled} ref={registerInput(requiredValidator)} name={name} onToggle={onToggle}>
          <DefaultToggle
            {...commonInputProps}
            title={renderProps?.value?.title || placeholder}
            $isAnimated={isComponentAnimated}
          >
            {renderProps?.value?.title || placeholder}
          </DefaultToggle>
          <Spiner style={{ display: isSpinerAnimated ? 'block' : 'none', left: '10px' }} />
          <Menu>{renderOptions(renderProps)}</Menu>
        </RacDropdown>
      </InputGroupUI>
    ),
    [
      showPlaceholder,
      commonInputProps,
      disabled,
      registerInput,
      name,
      placeholder,
      isComponentAnimated,
      isSpinerAnimated,
      renderOptions,
      onToggle,
    ],
  );

  return <Controller name={name} render={renderDropdown} control={controlField} rules={{ ...rules, validate }} />;
};
